import { TransitsDataType, TransitType } from 'types';

import { clone } from './formHandlers';

class Transits {
    private setTransits: (
        transits: TransitType[],
        transitsData: TransitsDataType | null,
        searchId: string,
        update?: boolean,
        force?: boolean
    ) => void;
    private searchId: string;

    constructor(
        setTransits: (transits: TransitType[], transitsData: TransitsDataType | null, searchId: string) => void,
        searchId: string
    ) {
        this.setTransits = setTransits;
        this.searchId = searchId;
    }

    private async patchOptions(
        transits: TransitType[],
        id: number | string,
        keys: (keyof TransitType['options'])[],
        values: any[]
    ): Promise<TransitType[]> {
        return new Promise((resolve) => {
            // remove clone and check the speed
            const updatedTransits = clone(transits).map((transit: TransitType) => {
                if (transit.id === parseInt(id as string, 10) || transit.id === id || id === '0') {
                    // build updated options in one pass
                    const newOptions = { ...transit.options };
                    keys.forEach((key, index) => {
                        newOptions[key] = values[index];
                    });
                    return {
                        ...transit,
                        options: newOptions,
                    };
                }
                return transit;
            });
            resolve(updatedTransits);
        });
    }

    private async updateOption(
        transits: TransitType[],
        id: number | string,
        key: keyof TransitType['options'],
        value: boolean
    ): Promise<TransitType[]> {
        return new Promise((resolve) => {
            const updatedTransits = clone(transits).map((transit) => ({
                ...transit,
                options: {
                    ...transit.options,
                    [key]:
                        transit.id === parseInt(id as string, 10) || transit.id === id || id === '0'
                            ? value
                            : transit.options[key],
                },
            }));
            resolve(updatedTransits);
        });
    }

    async updateOptions(
        oldTransits: TransitType[],
        newTransits: TransitType[],
        key: keyof TransitType['options'],
        value: boolean
    ): Promise<TransitType[]> {
        return new Promise((resolve) => {
            const updatedTransits = newTransits.map((newTransit) => {
                const oldTransit = oldTransits.find((old) => old.id === newTransit.id);
                return {
                    ...newTransit,
                    options: {
                        ...newTransit.options,
                        [key]: oldTransit ? oldTransit.options[key] : value,
                    },
                };
            });
            resolve(updatedTransits);
        });
    }

    async select(transits: TransitType[], id: number | string, value: boolean): Promise<TransitType[]> {
        const updated = await this.patchOptions(transits, id, ['select', 'display'], [value, value]);

        await this.setTransits(updated, null, this.searchId);
        return updated;
    }

    async selectFromArray(
        transits: TransitType[],
        ids: number[] | string[],
        values: boolean[]
    ): Promise<TransitType[]> {
        let updated = transits;

        for (let index = 0; index < ids.length; index++) {
            updated = await this.updateOption(updated, ids[index], 'select', values[index]);
            //REMOVE 2.1
            updated = await this.updateOption(updated, ids[index], 'display', values[index]);
        }

        await this.setTransits(updated, null, this.searchId);
        return updated;
    }
    async highlight(transits: TransitType[], id: number | string, value: boolean): Promise<TransitType[]> {
        const updated = await this.updateOption(transits, id, 'highlight', value);
        await this.setTransits(updated, null, this.searchId);
        return updated;
    }

    async display(transits: TransitType[], id: number | string, value: boolean): Promise<TransitType[]> {
        const updated = await this.patchOptions(transits, id, ['select', 'display'], [value, value]);
        await this.setTransits(updated, null, this.searchId);
        return updated;
    }

    async outline(transits: TransitType[], id: number | string, value: boolean): Promise<TransitType[]> {
        console.log(`INFO: Updating outline for transit with ID ${id}`);

        const updated = await this.patchOptions(transits, id, ['outline', 'oldOutline'], [value, value]);

        await this.setTransits(updated, null, this.searchId);
        return updated;
    }

    async sweptPath(transits: TransitType[], id: number | string, value: boolean): Promise<TransitType[]> {
        const updated = await this.updateOption(transits, id, 'drawSweptPath', value);
        await this.setTransits(updated, null, this.searchId);
        return updated;
    }

    async track(transits: TransitType[], id: number | string, value: boolean): Promise<TransitType[]> {
        const updated = await this.patchOptions(transits, id, ['drawTrack', 'oldDrawTrack'], [value, value]);

        await this.setTransits(updated, null, this.searchId);
        return updated;
    }

    async inspect(transits: TransitType[], id: number | string | null): Promise<TransitType[]> {
        console.time('inspect total');

        console.time('step 1: clone transits');
        const cloned = clone(transits);
        console.timeEnd('step 1: clone transits');

        console.time('step 2: map + update options');
        const updatedTransits = cloned.map((transit) => {
            const t0 = performance.now();

            // copy options
            let options = { ...transit.options };

            if (transit.id === id) {
                options = {
                    ...options,
                    oldDrawTrack: transit.options.drawTrack,
                    oldOutline: transit.options.outline,
                };
            }

            const updated = {
                ...transit,
                options: {
                    ...options,
                    drawTrack: transit.id === id ? true : options.oldDrawTrack,
                    outline: transit.id === id ? true : options.oldOutline,
                    inspect: transit.id === id,
                },
            };

            const t1 = performance.now();
            console.log(`  transit ${transit.id} update took ${(t1 - t0).toFixed(3)} ms`);

            return updated;
        });
        console.timeEnd('step 2: map + update options');

        console.time('step 3: setTransits');
        await this.setTransits(updatedTransits, null, this.searchId, true, true);
        console.timeEnd('step 3: setTransits');

        console.timeEnd('inspect total');
        return updatedTransits;
    }

    async expand(transits: TransitType[], id: number | string | null): Promise<TransitType[]> {
        for (const transit of clone(transits)) {
            transits = await this.updateOption(
                transits,
                transit.id,
                'expand',
                transit.id === id ? !transit.options.expand : false
            );
        }

        await this.setTransits(transits, null, this.searchId, true, true);
        return transits;
    }

    async displayAll(transits: TransitType[], ids: number[] | string[], value: boolean): Promise<TransitType[]> {
        transits = clone(transits).map((transit) => ({
            ...transit,
            options: {
                ...transit.options,
                //@ts-ignore
                select: ids.includes(transit.id) ? value : !value,
                //@ts-ignore
                display: ids.includes(transit.id) ? value : !value,
            },
        }));
        await this.setTransits(transits, null, this.searchId);
        return transits;
    }

    async highlightAll(transits: TransitType[], ids: number[] | string[], value: boolean): Promise<TransitType[]> {
        transits = clone(transits).map((transit) => ({
            ...transit,
            options: {
                ...transit.options,
                //@ts-ignore
                highlight: ids.includes(transit.id) ? value : !value,
            },
        }));
        await this.setTransits(transits, null, this.searchId);
        return transits;
    }

    async trackAll(transits: TransitType[], value: boolean): Promise<TransitType[]> {
        window?.log(`INFO : Swith OFF/ON all vessels tracks , ${value}`);
        transits = clone(transits).map((transit) => ({
            ...transit,
            options: {
                ...transit.options,
                drawTrack: value,
                oldDrawTrack: value,
            },
        }));
        await this.setTransits(transits, null, this.searchId);
        return transits;
    }

    async outlineAll(transits: TransitType[], value: boolean): Promise<TransitType[]> {
        window?.log('INFO : Switch OFF/ON all vessel outlines');

        // Clone and update the transits array
        transits = clone(transits).map((transit) => ({
            ...transit,
            options: {
                ...transit.options,
                outline: value,
                oldOutline: value,
            },
        }));

        // Set the transits after the map is ready
        this.setTransits(transits, null, this.searchId);

        return transits;
    }

    sweptPathAll(transits: TransitType[], value: boolean): TransitType[] {
        window?.log(`INFO : Swith OFF/ON all swept Paths - ${value}`);
        transits = clone(transits).map((transit) => ({
            ...transit,
            options: {
                ...transit.options,
                drawSweptPath: value,
            },
        }));
        this.setTransits(transits, null, this.searchId);
        return transits;
    }
}

export default Transits;
